import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  Radio,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import moment from "moment";
import { axiosInstance, fetchInstance } from "../../utils/networking";
import { useViewport } from "../../utils/useViewport";

// Merge-specific case picker + confirmation. Deliberately not built on
// ThriveCaseSelection: that one is shared by several list screens, and the
// merge flow needs a compact single-select list with a footer that always
// stays on screen.

const BRAND = "#9E1F63";
const BRAND_DARK = "#7d184e";
const PAGE_SIZE = 50;

type StatusFilter = "either" | "open" | "completed";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  currentCase: any;
  currentFirmName?: string;
}

const firmName = (c: any) =>
  c?.firmRecord?.["Company Name"] || "Unnamed company";

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

// One line from 600 px up; two lines on phones, where the row is narrow.
const clampSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: { xs: 2, sm: 1 },
  wordBreak: "break-word" as const,
};

const CaseRow: React.FC<{
  caseItem: any;
  selected: boolean;
  onSelect: () => void;
}> = ({ caseItem, selected, onSelect }) => {
  const name = firmName(caseItem);
  const sbc = caseItem.primarySbc || caseItem.createdBy;
  const industry = caseItem.firmRecord?.primaryIndustry;
  const meta = [
    caseItem.date ? `Created ${moment(caseItem.date).format("MMM D, YYYY")}` : "",
    sbc ? `SBC ${sbc}` : "",
    industry,
  ].filter(Boolean);

  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: "1px solid",
        borderColor: selected ? BRAND : "divider",
        bgcolor: selected ? "rgba(158,31,99,0.06)" : "background.paper",
        cursor: "pointer",
        transition: "border-color .15s, background-color .15s",
        "&:hover": { borderColor: selected ? BRAND : "grey.400" },
        "&:focus-visible": { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
      }}
    >
      <Radio
        checked={selected}
        tabIndex={-1}
        size="small"
        sx={{ p: 0.5, color: "grey.400", "&.Mui-checked": { color: BRAND } }}
      />
      <Avatar
        sx={{
          display: { xs: "none", sm: "flex" },
          width: 36,
          height: 36,
          fontSize: 14,
          fontWeight: 600,
          bgcolor: selected ? BRAND : "grey.200",
          color: selected ? "#fff" : "grey.700",
        }}
      >
        {initials(name)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 15, ...clampSx }} title={name}>
          {name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={clampSx}
          title={meta.join(" · ")}
        >
          {meta.join(" · ")}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={caseItem.status === "completed" ? "Closed" : "Open"}
        sx={{
          flexShrink: 0,
          fontWeight: 500,
          bgcolor: caseItem.status === "completed" ? "grey.100" : "#e8f5e9",
          color: caseItem.status === "completed" ? "grey.700" : "#2e7d32",
        }}
      />
    </Box>
  );
};

const MergeCase: React.FC<Props> = ({
  open,
  setOpen,
  currentCase,
  currentFirmName,
}) => {
  const { isPhone } = useViewport();
  const [search, setSearch] = React.useState("");
  const [appliedSearch, setAppliedSearch] = React.useState("");
  const [status, setStatus] = React.useState<StatusFilter>("either");
  const [cases, setCases] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const [selected, setSelected] = React.useState<any>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [merging, setMerging] = React.useState(false);
  const [mergeError, setMergeError] = React.useState("");

  const requestRef = React.useRef<AbortController | null>(null);
  const skipRef = React.useRef(0);
  const currentId = String(currentCase?._id ?? "");
  const currentName = currentFirmName || "this case";

  // Debounce typing into a server-side search.
  React.useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = React.useCallback(
    async (append: boolean) => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;
      const skip = append ? skipRef.current : 0;
      append ? setLoadingMore(true) : setLoading(true);
      setLoadError(false);

      try {
        const response = await fetchInstance(
          `${process.env.REACT_APP_API_DOMAIN}/cases/recent`,
          {
            method: "POST",
            body: JSON.stringify({
              period: "all",
              caseStatus: status,
              sortBy: "recent",
              primaryIndustry: "all",
              ids: [],
              sbc: "all",
              skip,
              limit: PAGE_SIZE,
              searchTerm: appliedSearch,
            }),
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        // NDJSON: one case per line.
        const rows = (await response.text())
          .split("\n")
          .filter((l) => l.trim())
          .map((l) => JSON.parse(l));
        if (controller.signal.aborted) return;

        skipRef.current = skip + rows.length;
        setHasMore(rows.length >= PAGE_SIZE);
        // A case can't be merged into itself.
        const pickable = rows.filter((c) => String(c._id) !== currentId);
        setCases((prev) => (append ? [...prev, ...pickable] : pickable));
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        console.error("Failed to load cases:", err);
        setLoadError(true);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [status, appliedSearch, currentId]
  );

  React.useEffect(() => {
    if (!open) return;
    setCases([]);
    skipRef.current = 0;
    fetchPage(false);
    return () => requestRef.current?.abort();
  }, [open, fetchPage]);

  // Reset between openings so a stale selection never carries over.
  React.useEffect(() => {
    if (!open) {
      setSelected(null);
      setSearch("");
      setAppliedSearch("");
      setStatus("either");
      setConfirmOpen(false);
      setMergeError("");
    }
  }, [open]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      hasMore &&
      !loading &&
      !loadingMore &&
      el.scrollHeight - el.scrollTop - el.clientHeight < 200
    ) {
      fetchPage(true);
    }
  };

  const handleMerge = () => {
    if (!selected) return;
    setMerging(true);
    setMergeError("");
    axiosInstance
      .post(`/cases/merge`, {
        keep_case: currentCase._id,
        delete_case: selected._id,
      })
      .then(() => {
        window.location.reload();
      })
      .catch((err) => {
        setMerging(false);
        setMergeError(
          err?.response?.data?.message
            ? `${err.response.data.message}. Please try again.`
            : "The merge didn't go through. Please try again."
        );
      });
  };

  const close = () => {
    if (!merging) setOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        fullWidth
        maxWidth="sm"
        fullScreen={isPhone}
        PaperProps={{
          sx: {
            borderRadius: isPhone ? 0 : 3,
            height: isPhone ? "100%" : "min(720px, calc(100% - 64px))",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "rgba(158,31,99,0.08)",
              color: BRAND,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <MergeTypeIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}>
              Merge a case
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={clampSx}>
              Pick the duplicate to merge into{" "}
              <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                {currentName}
              </Box>
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={close} sx={{ mt: -0.5, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Filters */}
        <Box
          sx={{
            px: 3,
            pb: 2,
            display: "flex",
            gap: 1.5,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            autoFocus={!isPhone}
            size="small"
            fullWidth
            placeholder="Search by company name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearch("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={status}
            onChange={(_, v) => v && setStatus(v)}
            sx={{
              flexShrink: 0,
              "& .MuiToggleButton-root": { textTransform: "none", px: 1.5 },
              "& .Mui-selected": {
                color: `${BRAND} !important`,
                bgcolor: "rgba(158,31,99,0.08) !important",
              },
            }}
          >
            <ToggleButton value="either">All</ToggleButton>
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="completed">Closed</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* List */}
        <Box
          onScroll={handleScroll}
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            bgcolor: "grey.50",
          }}
        >
          {loading ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress size={28} sx={{ color: BRAND }} />
            </Box>
          ) : loadError ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Couldn't load cases.
              </Typography>
              <Button onClick={() => fetchPage(false)} sx={{ color: BRAND }}>
                Retry
              </Button>
            </Box>
          ) : cases.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 600 }}>No matching cases</Typography>
              <Typography variant="body2" color="text.secondary">
                {appliedSearch
                  ? "Try a different name or status."
                  : "There are no other cases to merge."}
              </Typography>
            </Box>
          ) : (
            <>
              {cases.map((c) => (
                <CaseRow
                  key={c._id}
                  caseItem={c}
                  selected={selected?._id === c._id}
                  onSelect={() => setSelected(c)}
                />
              ))}
              {loadingMore && (
                <Box sx={{ py: 1.5, textAlign: "center" }}>
                  <CircularProgress size={20} sx={{ color: BRAND }} />
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer — always visible */}
        <Box
          sx={{
            px: 3,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
          >
            {selected ? (
              <>
                Selected:{" "}
                <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                  {firmName(selected)}
                </Box>
              </>
            ) : (
              "Select a case to continue"
            )}
          </Typography>
          <Button onClick={close} sx={{ color: "text.secondary", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={!selected}
            onClick={() => setConfirmOpen(true)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: BRAND,
              "&:hover": { bgcolor: BRAND_DARK },
            }}
          >
            Continue
          </Button>
        </Box>
      </Dialog>

      {/* Confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={() => !merging && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 3 } }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: "#fdecea",
            color: "#c62828",
            display: "grid",
            placeItems: "center",
            mb: 2,
          }}
        >
          <WarningAmberRoundedIcon />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
          Merge Cases?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          All activities, referrals, and client info from{" "}
          <Box component="strong" sx={{ color: "text.primary" }}>
            {firmName(selected)}
          </Box>{" "}
          will move to{" "}
          <Box component="strong" sx={{ color: "text.primary" }}>
            {currentName}
          </Box>
          . This can't be undone.
        </Typography>

        {/* From → Into */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
            mb: 3,
          }}
        >
          <Box sx={{ px: 2, py: 1.25 }}>
            <Typography variant="caption" color="text.secondary">
              Merged and removed
            </Typography>
            <Typography sx={{ fontWeight: 600 }} noWrap>
              {firmName(selected)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -12,
                left: 16,
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "grid",
                placeItems: "center",
              }}
            >
              <ArrowDownwardIcon sx={{ fontSize: 14, color: BRAND }} />
            </Box>
          </Box>
          <Box sx={{ px: 2, pt: 1.75, pb: 1.25, bgcolor: "rgba(158,31,99,0.05)" }}>
            <Typography variant="caption" color="text.secondary">
              Kept
            </Typography>
            <Typography sx={{ fontWeight: 600 }} noWrap>
              {currentName}
            </Typography>
          </Box>
        </Box>

        {mergeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {mergeError}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmOpen(false)}
            disabled={merging}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "text.primary",
              borderColor: "divider",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            color="error"
            onClick={handleMerge}
            disabled={merging}
            startIcon={
              merging ? <CircularProgress size={16} color="inherit" /> : undefined
            }
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {merging ? "Merging…" : "Merge"}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default MergeCase;
